import React from "react";
import CustomTooltip from "./CustomTooltip";

/**
 * Generic component to display a list of items.
 * - If the list is empty → shows a fallback value
 * - If the list length is <= maxInline → renders items inline
 * - If the list is larger → shows a tooltip with all items
 *
 * @param {Object} props
 * @param {Array<any>} props.items - List of elements to render
 * @param {Function} props.renderItem - Function to render each item label (default: item.fullName or item)
 * @param {React.ReactNode} props.icon - Icon displayed when list exceeds maxInline (used as tooltip trigger)
 * @param {number} props.maxInline - Maximum number of items to render inline before collapsing into tooltip
 * @param {React.ReactNode|string} props.emptyValue - Value displayed when items list is empty
 * @param {string} props.className - CSS class applied to icons
 * @param {React.ReactNode} props.itemIcon - Icon displayed next to each item (inline mode)
 *
 * @returns {JSX.Element|string}
 */
const ListDisplayWithTooltip = ({
                                    items = [],
                                    renderItem = (item) => item?.fullName || item,
                                    icon = null,
                                    maxInline = 2,
                                    emptyValue = "-",
                                    className = "icon-color-general-950",
                                    itemIcon = null,
                                }) => {

    // Empty state
    if (!items.length) return emptyValue;

    // Inline rendering when items are within limit
    if (items.length <= maxInline) {
        return (
            <div className="d-flex flex-column">
                {items.map((item, index) => (
                    <div key={index} className="d-flex align-items-center">
                        {itemIcon && (
                            <div className="mr-8px">
                                {itemIcon}
                            </div>
                        )}
                        <span>{renderItem(item)}</span>
                    </div>
                ))}
            </div>
        );
    }

    // Tooltip rendering when items exceed limit
    return (
        <div className="d-flex align-items-center">
            {icon && (
                <CustomTooltip
                    text={
                        <ul className="m-0 p-0 ps-3">
                            {items.map((item, i) => (
                                <li key={i} className="text-capitalize">
                                    • {renderItem(item)}
                                </li>
                            ))}
                        </ul>
                    }
                >
                    <span className={`mr-8px ${className}`}>
                        {icon}
                    </span>
                </CustomTooltip>
            )}

            {itemIcon && (
                <span className={className}>
                    {itemIcon}
                </span>
            )}
        </div>
    );
};

export default ListDisplayWithTooltip;
